import { FC, useState, useCallback } from 'react';
import { DualDateRangePicker, Typography } from '@crisellevaldez/speculo-ui';
import { PillTabs } from '../../common/Tabs';
import { HiMail, HiPhone, HiViewGrid } from 'react-icons/hi';
import { useAuthStore } from '../../../contexts/auth/useAuthStore';
import { formatNumber } from '../../../utils/format';
import { useLiveTransfersCount } from '../../../hooks/contacts/useLiveTransfersCount';
import { CallStatsModal } from './CallStatsModal';
import { LiveTransfersModal } from './LiveTransfersModal';
import { ContactsCalledModal } from './ContactsCalledModal';
import { HandoffsStatsModal } from '../dashboard/HandoffsStatsModal';
import { useStats } from '../../../hooks/useStats';
import { useHandoffStats } from '../../../hooks/dashboard/handoff';
import { useOrganizationStore, useFlywheelEnabled } from '../../../stores/useOrganizationStore';
import {
  DateRange,
  convertDateRangeForDisplay,
  handleDateRangeChange,
  getDefaultDateRange,
} from '../../../utils/dateRangeTimezone';
import { FollowUpBanner } from '../dashboard/FollowUpBanner';

interface StatsBoxProps {
  title?: string;
  showDashboardSwitch?: boolean;
  currentView?: string;
  onViewChange?: (value: string | string[]) => void;
  description?: string;
}

const LoadingDots: FC = () => (
  <span className="inline-flex">
    <span className="animate-[loading_1.4s_ease-in-out_infinite] opacity-0">.</span>
    <span className="animate-[loading_1.4s_ease-in-out_0.2s_infinite] opacity-0">.</span>
    <span className="animate-[loading_1.4s_ease-in-out_0.4s_infinite] opacity-0">.</span>
  </span>
);

export const StatsBox: FC<StatsBoxProps> = ({
  title,
  showDashboardSwitch = false,
  currentView = 'all',
  onViewChange,
  description,
}) => {
  const [modalType, setModalType] = useState<'all' | 'answered' | 'opportunities' | null>(null);
  const [isContactsCalledModalOpen, setIsContactsCalledModalOpen] = useState(false);
  const [isLiveTransfersModalOpen, setIsLiveTransfersModalOpen] = useState(false);
  const [showHandoffs, setShowHandoffs] = useState(false);
  const [showClaimedHandoffs, setShowClaimedHandoffs] = useState(false);
  const [showHoldHandoffs, setShowHoldHandoffs] = useState(false);
  const [showReturnedToRemiHandoffs, setShowReturnedToRemiHandoffs] = useState(false);
  const organization = useOrganizationStore((state) => state.organization);
  const flywheelEnabled = useFlywheelEnabled();

  // Function to get default date range
  const getDefaultDateRangeCallback = useCallback(() => {
    return getDefaultDateRange(organization?.timezone || 'UTC');
  }, [organization?.timezone]);

  // Initialize dates in organization timezone with 1 month range
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRangeCallback);

  // Memoize date change handler to prevent unnecessary recreations
  const handleDateChange = useCallback(
    (newRange: DateRange) => {
      const result = handleDateRangeChange(
        newRange,
        organization?.timezone,
        getDefaultDateRangeCallback
      );
      setDateRange(result.dateRange);
    },
    [organization?.timezone, getDefaultDateRangeCallback]
  );

  const {
    callsMade,
    contactsCalled,
    conversations,
    totalCallDuration,
    opportunities,
    isLoading,
    getFilteredCalls,
  } = useStats(dateRange);
  const liveTransfers = useLiveTransfersCount(dateRange);

  // Get handoff stats when flywheel is enabled
  const { stats: handoffStats, isLoading: handoffStatsLoading } = useHandoffStats({
    dateRange,
    enabled: flywheelEnabled,
  });

  // Calculate percentages
  const conversationPercentage =
    contactsCalled > 0 ? Math.round((conversations / contactsCalled) * 100) : 0;

  const opportunityPercentage =
    conversations > 0 ? Math.round((opportunities / conversations) * 100) : 0;

  const { user } = useAuthStore();

  return (
    <div className="flex flex-col gap-5 xl:gap-10">
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 2xl:gap-6">
            <Typography
              variant="h1"
              className="text-gray-950 whitespace-nowrap text-2xl 2xl:text-3xl font-black  min-w-[210px]"
            >
              {title || `Hi, ${user?.displayName || 'there'}`}
            </Typography>
            {showDashboardSwitch && onViewChange && (
              <PillTabs
                value={currentView}
                onChange={onViewChange}
                tabs={[
                  {
                    id: 'all',
                    label: 'Main',
                    icon: <HiViewGrid className="w-4 h-4" />,
                  },
                  {
                    id: 'email',
                    label: 'Email',
                    icon: <HiMail className="w-4 h-4" />,
                  },
                  {
                    id: 'calls',
                    label: 'Calls',
                    icon: <HiPhone className="w-4 h-4" />,
                  },
                ]}
              />
            )}
          </div>
        </div>
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-5">
          {description && (
            <p className="text-gray-800 text-sm xl:text-base leading-relaxed mt-2 3xl:max-w-[1200px]">
              {description}
            </p>
          )}
          <div className="flex flex-col gap-1">
            <label className="text-sm text-gray-700 font-medium">
              Statistics Date Range
              {organization?.timezone && (
                <span className="text-xs text-gray-500 ml-2">({organization.timezone})</span>
              )}
            </label>
            <div className="w-fit">
              <DualDateRangePicker
                value={convertDateRangeForDisplay(dateRange, organization?.timezone)}
                onChange={handleDateChange}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Stats section with Follow-Up Banner */}
      <div className="flex flex-col gap-4">
        {/* Follow-Up Reminders Banner */}
        <FollowUpBanner dateRange={dateRange} />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
          <div
            role={callsMade > 0 ? 'button' : undefined}
            tabIndex={callsMade > 0 ? 0 : undefined}
            onClick={callsMade > 0 ? () => setModalType('all') : undefined}
            onKeyDown={callsMade > 0 ? (e) => e.key === 'Enter' && setModalType('all') : undefined}
            className={`
            rounded-xl p-4 md:p-5 border flex flex-col justify-center min-h-[120px] transition-all duration-300 shadow-lg
            bg-gradient-to-br from-white to-gray-50 border-gray-300
            ${
              callsMade > 0
                ? 'cursor-pointer hover:shadow-xl hover:translate-y-[-2px] hover:bg-gradient-to-br hover:from-amber-50 hover:to-amber-100 hover:border-amber-300'
                : 'cursor-not-allowed'
            }
          `}
          >
            <div className="mb-3">
              <span className="text-xs md:text-[13px] 2xl:text-sm text-gray-700 font-semibold tracking-wide uppercase">
                Calls Made
              </span>
            </div>
            <div className="text-3xl xl:text-[32px] 3xl:text-[36px] font-extrabold text-primaryfortext transition-all duration-300 animate-fadeIn">
              {isLoading ? <LoadingDots /> : formatNumber(callsMade)}
            </div>
          </div>

          <div
            role={contactsCalled > 0 ? 'button' : undefined}
            tabIndex={contactsCalled > 0 ? 0 : undefined}
            onClick={contactsCalled > 0 ? () => setIsContactsCalledModalOpen(true) : undefined}
            onKeyDown={
              contactsCalled > 0
                ? (e) => e.key === 'Enter' && setIsContactsCalledModalOpen(true)
                : undefined
            }
            className={`
            rounded-xl p-4 md:p-5 border flex flex-col justify-center min-h-[120px] transition-all duration-300 shadow-lg
            bg-gradient-to-br from-white to-gray-50 border-gray-300
            ${
              contactsCalled > 0
                ? 'cursor-pointer hover:shadow-xl hover:translate-y-[-2px] hover:bg-gradient-to-br hover:from-amber-50 hover:to-amber-100 hover:border-amber-300'
                : 'cursor-not-allowed'
            }
          `}
          >
            <div className="mb-3">
              <span className="text-xs md:text-[13px] 2xl:text-sm text-gray-700 font-semibold tracking-wide uppercase">
                Contacts Called
              </span>
            </div>
            <div className="text-3xl xl:text-[32px] 3xl:text-[36px] font-extrabold text-primaryfortext transition-all duration-300 animate-fadeIn">
              {isLoading ? <LoadingDots /> : formatNumber(contactsCalled)}
            </div>
          </div>

          <div
            role={conversations > 0 ? 'button' : undefined}
            tabIndex={conversations > 0 ? 0 : undefined}
            onClick={conversations > 0 ? () => setModalType('answered') : undefined}
            onKeyDown={
              conversations > 0 ? (e) => e.key === 'Enter' && setModalType('answered') : undefined
            }
            className={`
            rounded-xl p-4 md:p-5 border flex flex-col justify-center min-h-[120px] transition-all duration-300 shadow-lg
            bg-gradient-to-br from-amber-50 to-amber-100 border-amber-300
            ${
              conversations > 0
                ? 'cursor-pointer hover:shadow-xl hover:translate-y-[-2px]'
                : 'cursor-not-allowed'
            }
          `}
          >
            <div className="mb-3">
              <span className="text-xs md:text-[13px] 2xl:text-sm text-gray-700 font-semibold tracking-wide uppercase">
                Conversations
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl xl:text-[32px] 3xl:text-[36px] font-extrabold text-primaryfortext transition-all duration-300 animate-fadeIn">
                {isLoading ? <LoadingDots /> : formatNumber(conversations)}
              </span>
              <span className="text-sm text-gray-600 font-medium ml-1 animate-fadeIn">
                {isLoading ? <LoadingDots /> : `${conversationPercentage}%`}
              </span>
            </div>
            <div className="mt-1 animate-fadeIn">
              <div className="flex flex-col 3xl:flex-row items-start 3xl:items-center 3xl:gap-1 text-gray-500">
                <span className="text-xs font-medium">Total Call Duration:</span>
                <span className="text-sm text-gray-600 font-semibold">
                  {isLoading ? <LoadingDots /> : totalCallDuration}
                </span>
              </div>
            </div>
          </div>

          {flywheelEnabled ? (
            <div
              role={handoffStats.pending > 0 ? 'button' : undefined}
              tabIndex={handoffStats.pending > 0 ? 0 : undefined}
              onClick={handoffStats.pending > 0 ? () => setShowHandoffs(true) : undefined}
              onKeyDown={
                handoffStats.pending > 0
                  ? (e) => e.key === 'Enter' && setShowHandoffs(true)
                  : undefined
              }
              className={`
              rounded-xl p-4 md:p-5 border flex flex-col justify-center min-h-[120px] transition-all duration-300 shadow-lg
              bg-gradient-to-br from-white to-gray-50 border-gray-300
              ${
                handoffStats.pending > 0
                  ? 'cursor-pointer hover:shadow-xl hover:translate-y-[-2px] hover:bg-gradient-to-br hover:from-amber-50 hover:to-amber-100 hover:border-amber-300'
                  : 'cursor-not-allowed'
              }
            `}
            >
              <div className="mb-3 flex items-center gap-2">
                <span className="text-xs md:text-[13px] 2xl:text-sm text-gray-700 font-semibold tracking-wide uppercase">
                  Handoffs
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl xl:text-[32px] 3xl:text-[36px] font-extrabold text-primaryfortext transition-all duration-300 animate-fadeIn">
                  {isLoading || handoffStatsLoading ? (
                    <LoadingDots />
                  ) : (
                    formatNumber(handoffStats.pending)
                  )}
                </span>
                <span className="text-sm text-gray-600 font-medium ml-1 animate-fadeIn">
                  Available to Claim
                </span>
              </div>
              <div className="flex flex-col 3xl:flex-row items-start 3xl:items-center 3xl:gap-4 mt-1">
                <div className="flex items-center gap-1 text-gray-500">
                  <button
                    className="text-xs font-medium cursor-pointer hover:text-primary hover:underline focus:outline-none"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (handoffStats.claimed > 0) {
                        setShowClaimedHandoffs(true);
                      }
                    }}
                  >
                    Claimed:
                  </button>
                  <button
                    className="text-sm text-gray-600 font-semibold cursor-pointer hover:text-primary hover:underline focus:outline-none"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (handoffStats.claimed > 0) {
                        setShowClaimedHandoffs(true);
                      }
                    }}
                  >
                    {isLoading || handoffStatsLoading ? (
                      <LoadingDots />
                    ) : (
                      formatNumber(handoffStats.claimed)
                    )}
                  </button>
                </div>
                <div className="flex items-center gap-1 text-gray-500">
                  <button
                    className="text-xs font-medium cursor-pointer hover:text-primary hover:underline focus:outline-none"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (handoffStats.hold > 0) {
                        setShowHoldHandoffs(true);
                      }
                    }}
                  >
                    Hold:
                  </button>
                  <button
                    className="text-sm text-gray-600 font-semibold cursor-pointer hover:text-primary hover:underline focus:outline-none"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (handoffStats.hold > 0) {
                        setShowHoldHandoffs(true);
                      }
                    }}
                  >
                    {isLoading || handoffStatsLoading ? (
                      <LoadingDots />
                    ) : (
                      formatNumber(handoffStats.hold)
                    )}
                  </button>
                </div>
                <div className="flex items-center gap-1 text-gray-500">
                  <button
                    className="text-xs font-medium cursor-pointer hover:text-primary hover:underline focus:outline-none"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (handoffStats.released > 0) {
                        setShowReturnedToRemiHandoffs(true);
                      }
                    }}
                  >
                    Returned:
                  </button>
                  <button
                    className="text-sm text-gray-600 font-semibold cursor-pointer hover:text-primary hover:underline focus:outline-none"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (handoffStats.released > 0) {
                        setShowReturnedToRemiHandoffs(true);
                      }
                    }}
                  >
                    {isLoading || handoffStatsLoading ? (
                      <LoadingDots />
                    ) : (
                      formatNumber(handoffStats.released)
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div
              role={opportunities > 0 ? 'button' : undefined}
              tabIndex={opportunities > 0 ? 0 : undefined}
              onClick={opportunities > 0 ? () => setModalType('opportunities') : undefined}
              onKeyDown={
                opportunities > 0
                  ? (e) => e.key === 'Enter' && setModalType('opportunities')
                  : undefined
              }
              className={`
              rounded-xl p-4 md:p-5 border flex flex-col justify-center min-h-[120px] transition-all duration-300 shadow-lg
              bg-gradient-to-br from-white to-gray-50 border-gray-300
              ${
                opportunities > 0
                  ? 'cursor-pointer hover:shadow-xl hover:translate-y-[-2px] hover:bg-gradient-to-br hover:from-amber-50 hover:to-amber-100 hover:border-amber-300'
                  : 'cursor-not-allowed'
              }
            `}
            >
              <div className="mb-3">
                <span className="text-xs md:text-[13px] 2xl:text-sm text-gray-700 font-semibold tracking-wide uppercase">
                  Opportunities
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl xl:text-[32px] 3xl:text-[36px] font-extrabold text-primaryfortext transition-all duration-300 animate-fadeIn">
                  {isLoading ? <LoadingDots /> : formatNumber(opportunities)}
                </span>
                <span className="text-sm text-gray-600 font-medium ml-1 animate-fadeIn">
                  {isLoading ? <LoadingDots /> : `${opportunityPercentage}%`}
                </span>
              </div>
            </div>
          )}

          <div
            role={liveTransfers.count > 0 ? 'button' : undefined}
            tabIndex={liveTransfers.count > 0 ? 0 : undefined}
            onClick={liveTransfers.count > 0 ? () => setIsLiveTransfersModalOpen(true) : undefined}
            onKeyDown={
              liveTransfers.count > 0
                ? (e) => e.key === 'Enter' && setIsLiveTransfersModalOpen(true)
                : undefined
            }
            className={`
            rounded-xl p-4 md:p-5 border flex flex-col justify-center min-h-[120px] transition-all duration-300 shadow-lg
            bg-gradient-to-br from-white to-gray-50 border-gray-300
            ${
              liveTransfers.count > 0
                ? 'cursor-pointer hover:shadow-xl hover:translate-y-[-2px] hover:bg-gradient-to-br hover:from-amber-50 hover:to-amber-100 hover:border-amber-300'
                : 'cursor-not-allowed'
            }
          `}
          >
            <div className="mb-3">
              <span className="text-xs md:text-[13px] 2xl:text-sm text-gray-700 font-semibold tracking-wide uppercase">
                Live Transfers
              </span>
            </div>
            <div className="text-3xl xl:text-[32px] 3xl:text-[36px] font-extrabold text-primaryfortext transition-all duration-300 animate-fadeIn">
              {isLoading || liveTransfers.isLoading ? (
                <LoadingDots />
              ) : (
                formatNumber(liveTransfers.count)
              )}
            </div>
          </div>
        </div>
      </div>
      {modalType && (
        <CallStatsModal
          isOpen={modalType !== null}
          onClose={() => setModalType(null)}
          dateRange={dateRange}
          getFilteredCalls={getFilteredCalls}
          orgTimezone={organization?.timezone}
          type={modalType}
          callsMade={callsMade}
          conversations={conversations}
          opportunities={opportunities}
        />
      )}
      {organization?.id && (
        <ContactsCalledModal
          isOpen={isContactsCalledModalOpen}
          onClose={() => setIsContactsCalledModalOpen(false)}
          organization_id={organization.id}
          dateRange={dateRange}
          contactsCalled={contactsCalled}
          orgTimezone={organization?.timezone}
          getFilteredCalls={getFilteredCalls}
        />
      )}
      {organization?.id && (
        <LiveTransfersModal
          isOpen={isLiveTransfersModalOpen}
          onClose={() => setIsLiveTransfersModalOpen(false)}
          dateRange={dateRange}
          orgTimezone={organization?.timezone}
          transferCount={liveTransfers.count}
        />
      )}
      {/* Handoffs Modals */}
      {showHandoffs && (
        <HandoffsStatsModal
          isOpen={showHandoffs}
          onClose={() => setShowHandoffs(false)}
          dateRange={dateRange}
          orgTimezone={organization?.timezone}
          handoffCount={handoffStats.pending}
          status="pending"
        />
      )}
      {showClaimedHandoffs && (
        <HandoffsStatsModal
          isOpen={showClaimedHandoffs}
          onClose={() => setShowClaimedHandoffs(false)}
          dateRange={dateRange}
          orgTimezone={organization?.timezone}
          handoffCount={handoffStats.claimed}
          status="claimed"
        />
      )}
      {showHoldHandoffs && (
        <HandoffsStatsModal
          isOpen={showHoldHandoffs}
          onClose={() => setShowHoldHandoffs(false)}
          dateRange={dateRange}
          orgTimezone={organization?.timezone}
          handoffCount={handoffStats.hold}
          status="hold"
        />
      )}
      {showReturnedToRemiHandoffs && (
        <HandoffsStatsModal
          isOpen={showReturnedToRemiHandoffs}
          onClose={() => setShowReturnedToRemiHandoffs(false)}
          dateRange={dateRange}
          orgTimezone={organization?.timezone}
          handoffCount={handoffStats.released}
          status="released"
        />
      )}
    </div>
  );
};
